/**
 * CSV Preview Component
 * Displays CSV content in a table format with import button
 */

import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { FileSpreadsheet, Upload, AlertCircle, Loader2 } from "lucide-react";
import { useLocation } from "wouter";

interface CSVPreviewProps {
  url: string;
  fileName: string;
  companyId?: number;
}

export function CSVPreview({ url, fileName, companyId }: CSVPreviewProps) {
  const [, setLocation] = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [csvData, setCsvData] = useState<{
    headers: string[];
    rows: string[][];
    totalRows: number;
  } | null>(null);
  const [delimiter, setDelimiter] = useState<"," | ";">(",");

  useEffect(() => {
    loadCSV();
  }, [url]);

  const loadCSV = async () => {
    setLoading(true);
    setError(null);

    try {
      // Check if URL is a Firebase Storage gs:// URL
      if (url.startsWith("gs://")) {
        throw new Error(
          'CSV preview is not available for Firebase Storage files. Use "Import Transactions" to process this file.'
        );
      }

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error("Failed to fetch CSV file");
      }

      const text = await response.text();
      parseCSV(text);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load CSV");
    } finally {
      setLoading(false);
    }
  };

  const parseCSV = (text: string) => {
    // Detect delimiter (semicolon or comma)
    const firstLine = text.split("\n")[0];
    const detectedDelimiter = firstLine.includes(";") ? ";" : ",";
    setDelimiter(detectedDelimiter);

    // Split into lines
    const lines = text.split("\n").filter(line => line.trim());

    if (lines.length === 0) {
      setError("CSV file is empty");
      return;
    }

    // Find header row (skip metadata rows that start with special characters)
    let headerIndex = 0;
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      // Skip lines that look like metadata (key:value format)
      if (
        line.includes(":") &&
        !line.includes(detectedDelimiter + detectedDelimiter)
      ) {
        continue;
      }
      headerIndex = i;
      break;
    }

    // Parse header
    const headers = lines[headerIndex]
      .split(detectedDelimiter)
      .map(h => h.trim().replace(/^["']|["']$/g, ""));

    // Parse data rows (show first 100 for preview)
    const dataRows = lines
      .slice(headerIndex + 1, headerIndex + 101)
      .map(line =>
        line
          .split(detectedDelimiter)
          .map(cell => cell.trim().replace(/^["']|["']$/g, ""))
      );

    setCsvData({
      headers,
      rows: dataRows,
      totalRows: lines.length - headerIndex - 1,
    });
  };

  const handleImport = () => {
    // Navigate to enhanced import with pre-loaded file URL
    const params = new URLSearchParams({
      fileUrl: url,
      fileName: fileName,
    });
    if (companyId) {
      params.append("companyId", companyId.toString());
    }
    setLocation(`/bank-import?${params.toString()}`);
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-center gap-4 py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            <p className="text-muted-foreground">Loading CSV preview...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
        {url.startsWith("gs://") && (
          <div className="flex justify-center">
            <Button onClick={handleImport} className="gap-2">
              <Upload className="h-4 w-4" />
              Import Transactions
            </Button>
          </div>
        )}
      </div>
    );
  }

  if (!csvData) {
    return null;
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FileSpreadsheet className="h-6 w-6 text-primary" />
              <div>
                <CardTitle>CSV Preview</CardTitle>
                <CardDescription>
                  {fileName} • {csvData.totalRows} rows • Delimiter:{" "}
                  {delimiter === ";" ? "Semicolon" : "Comma"}
                </CardDescription>
              </div>
            </div>
            <Button onClick={handleImport} className="gap-2">
              <Upload className="h-4 w-4" />
              Import Transactions
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {csvData.totalRows > 100 && (
            <Alert className="mb-4">
              <AlertDescription>
                Showing first 100 rows of {csvData.totalRows} total rows
              </AlertDescription>
            </Alert>
          )}

          <div className="border rounded-lg overflow-hidden">
            <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
              <Table>
                <TableHeader className="sticky top-0 bg-background z-10">
                  <TableRow>
                    <TableHead className="w-12 text-center">#</TableHead>
                    {csvData.headers.map((header, idx) => (
                      <TableHead key={idx} className="min-w-[150px]">
                        {header || `Column ${idx + 1}`}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {csvData.rows.map((row, rowIdx) => (
                    <TableRow key={rowIdx}>
                      <TableCell className="text-center text-muted-foreground font-mono text-sm">
                        {rowIdx + 1}
                      </TableCell>
                      {row.map((cell, cellIdx) => (
                        <TableCell key={cellIdx} className="font-mono text-sm">
                          {cell || (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>

          <div className="mt-4 flex items-center justify-between text-sm text-muted-foreground">
            <div className="flex gap-4">
              <span>Columns: {csvData.headers.length}</span>
              <span>Rows: {csvData.totalRows}</span>
            </div>
            <Badge variant="secondary">
              {delimiter === ";" ? "Swiss Format" : "Standard Format"}
            </Badge>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
